import { formatLogFields } from 'utils/webRtcNackStats';

// BEAST-13647: NACK is disabled on Kurento streams for the desks, locations, gates and cameras configured in the
// kurentoNackConfigEnabled feature flag, to stop retransmission bursts on unstable networks.

// The generic NACK line only. "a=rtcp-fb:<pt> nack pli" is the keyframe request and must stay.
const GENERIC_NACK_LINE_PATTERN = String.raw`^a=rtcp-fb:\S+ nack[ \t]*(?:\r?\n|$)`;
const GENERIC_NACK_LINES = new RegExp(GENERIC_NACK_LINE_PATTERN, 'gm');
const GENERIC_NACK_LINE = new RegExp(GENERIC_NACK_LINE_PATTERN, 'm');

const WILDCARD = '*';
const NO_MATCH = Object.freeze({ disabled: false, matchedBy: null, matchedKey: null, excludedBy: null });

export function stripNackFromSdp(sdp) {
    return typeof sdp === 'string' ? sdp.replace(GENERIC_NACK_LINES, '') : sdp;
}

// Whether the SDP enables generic NACK; undefined when there is no SDP yet.
export function isNackNegotiated(sdp) {
    if (typeof sdp !== 'string' || !sdp) return undefined;
    return GENERIC_NACK_LINE.test(sdp);
}

// config shape (the flag's `config` column):
// {
//   "deskIds": {
//     "<deskId>": {
//       "locationNames": { "<locationName>" | "*": { "exclude": { "cameraTypes": [...], "cameraIds": [...] } } },
//       "gateIds": { "<gateId>": { "exclude": {...} } },
//       "cameraIds": [<camId>, ...]
//     },
//     "<deskId>": ["<locationName>" | "*", ...]   <- short form of locationNames, no per-entry exclude
//   },
//   "exclude": { "cameraTypes": [...], "cameraIds": [...] }   <- general exclude, applies everywhere
// }
// The most specific match wins: cameraIds, then gateIds, then the named location, then "*". NACK is disabled
// unless the camera is in the general exclude or in the matched entry's exclude. Location names and camera types
// are compared ignoring case and surrounding spaces, ids as strings. Missing or malformed parts never disable NACK.
// "*" is a wildcard only in locationNames (every location of the desk). In gateIds it is an ordinary key that no
// gate id matches, so a "*" gate entry is ignored.
export function getNackDecision(config, { deskId, locationName, gateId, cameraType, cameraId } = {}) {
    if (!isPlainObject(config)) return NO_MATCH;
    const desk = resolveDesk(config.deskIds, deskId);
    if (!desk) return NO_MATCH;
    const match = pickMatch(desk, { locationName, gateId, cameraId });
    if (!match) return NO_MATCH;

    const camera = { cameraType: normalizeName(cameraType), cameraId: normalizeId(cameraId) };
    const excludedBy =
        findExclusion(config.exclude, 'general', camera) ??
        findExclusion(isPlainObject(match.entry) ? match.entry.exclude : undefined, `${match.matchedBy}.exclude`, camera);

    return { disabled: !excludedBy, matchedBy: match.matchedBy, matchedKey: match.matchedKey, excludedBy: excludedBy ?? null };
}

// [WEBRTC NACK CONFIG] line for a camera the config matched, whether NACK ended up disabled or kept by an exclude
export function formatNackDecisionLine(decision, context = {}) {
    const fields = {
        decision: decision?.disabled ? 'disabled' : 'kept',
        matchedBy: decision?.matchedBy,
        matchedKey: decision?.matchedKey,
        excludedBy: decision?.excludedBy,
        ...context,
    };
    return `[WEBRTC NACK CONFIG] ${formatLogFields(fields)}`;
}

function resolveDesk(deskIds, deskId) {
    const id = normalizeId(deskId);
    if (!isPlainObject(deskIds) || !id) return null;
    const key = Object.keys(deskIds).find((candidate) => normalizeId(candidate) === id);
    const desk = key === undefined ? undefined : deskIds[key];

    if (Array.isArray(desk)) {
        return {
            cameraIds: new Set(),
            gateIds: new Map(),
            locationNames: toEntryMap(
                desk.map((name) => [name, null]),
                normalizeName
            ),
        };
    }
    if (!isPlainObject(desk)) return null;
    return {
        cameraIds: toSet(desk.cameraIds, normalizeId),
        gateIds: toEntryMap(objectEntries(desk.gateIds), normalizeId),
        locationNames: toEntryMap(objectEntries(desk.locationNames), normalizeName),
    };
}

function pickMatch(desk, { locationName, gateId, cameraId }) {
    const camId = normalizeId(cameraId);
    if (camId && desk.cameraIds.has(camId)) return { matchedBy: 'cameraIds', matchedKey: camId, entry: null };

    const gate = desk.gateIds.get(normalizeId(gateId));
    if (gate) return { matchedBy: 'gateIds', ...gate };

    const location = desk.locationNames.get(normalizeName(locationName)) || desk.locationNames.get(WILDCARD);
    if (location) return { matchedBy: 'locationNames', ...location };

    return null;
}

// "<scope>.cameraIds" / "<scope>.cameraTypes" for the list that excludes the camera, null when none does
function findExclusion(exclude, scope, { cameraType, cameraId }) {
    if (!isPlainObject(exclude)) return null;
    if (cameraId && toSet(exclude.cameraIds, normalizeId).has(cameraId)) return `${scope}.cameraIds`;
    if (cameraType && toSet(exclude.cameraTypes, normalizeName).has(cameraType)) return `${scope}.cameraTypes`;
    return null;
}

// [key, entry] pairs -> Map(normalized key -> { matchedKey: the key as configured, entry }); invalid keys are dropped
function toEntryMap(pairs, normalize) {
    const map = new Map();
    for (const [key, entry] of pairs) {
        const normalized = normalize(key);
        if (normalized && !map.has(normalized)) map.set(normalized, { matchedKey: String(key).trim(), entry });
    }
    return map;
}

function objectEntries(value) {
    return isPlainObject(value) ? Object.entries(value) : [];
}

function toSet(list, normalize) {
    if (!Array.isArray(list)) return new Set();
    return new Set(list.filter(isListItem).map(normalize).filter(Boolean));
}

function isListItem(value) {
    return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));
}

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeId(value) {
    return isListItem(value) ? String(value).trim() : '';
}

function normalizeName(value) {
    return isListItem(value) ? String(value).trim().toLowerCase() : '';
}
